import async from 'async';
import _ from 'underscore';

import {
  KioskAvailability,
  KioskNotificationType,
  LocationState,
  LocationStateHelper,
} from '@biteinc/enums';
import { I9nHelper, Time } from '@biteinc/helpers';

import { convertCsvLinesToCsvData } from '../../csv_builder';
import { buildOrderChannelTag, i9nBadgesHtml } from '../../helpers/html_builder';
import { TimeHelper } from '../../helpers/time_helper';
import { getCsvLinesFromKioskList } from '../../kiosk_list_csv_builder';
import { template } from '../../template';

// BITE-7827 & BITE-10020
// Expo printers that are blacklisted should not be displayed in the kiosk list
const blacklistedExpoPrinters = ['X6WY393186'];

app.KioskListView = app.BaseListView.extend({
  turnOffDropdownTemplate: template($('#turnoff-dropdown-template').html()),

  initialize() {
    app.BaseListView.prototype.initialize.apply(this, arguments);

    if (this.collection.getMainCollection) {
      this._kioskList = this.collection.getMainCollection();
    } else {
      this._kioskList = this.collection;
    }
  },

  destroy() {
    app.BaseListView.prototype.apply.destroy(this, arguments);
    this.stopListening(this._kioskList);
  },

  hasFetchedAllData() {
    let hasFetched = app.BaseListView.prototype.hasFetchedAllData.apply(this);
    if (!app.org) {
      hasFetched = hasFetched && app.orgList.hasBeenFetched();
    } else if (!app.location) {
      hasFetched = hasFetched && app.locationList.hasBeenFetched();
    }
    return hasFetched;
  },

  _clickHandlerForAvailability($dropdown, availability, verb, processName) {
    const turningOn = availability === KioskAvailability.ON;
    const confirmation = `Are you sure you want to turn ${turningOn ? 'on' : 'off'} all kiosks${
      turningOn ? '' : ` ${verb.toLowerCase()}`
    }?`;
    if (window.confirm(confirmation)) {
      app.showSavedToast(processName);
      this._kioskList.changeAvailability(availability, {
        error() {
          app.showSavedToast('Failed. :(');
        },
        success() {
          app.showSavedToastAndReload('Success! Reloading in 10sec.', true, 10000);
        },
      });
      $dropdown.toggleClass('open');
    }
    return false;
  },

  __navigateToModel(kiosk) {
    this.scrollToItemWithId(kiosk.id, true);
    this.setSelectedModelId(kiosk.id);
    if (this._cellById[kiosk.id]) {
      this._cellById[kiosk.id].setSelected(true);
    }
  },

  getListSections() {
    let sections = [];
    if (app.location) {
      // Only happens at a location
      sections.push({ models: this._kioskList.models });
      if (app.expoPrinterList.models.length) {
        const filteredExpoPrinterList = app.expoPrinterList.filter(
          (model) => !blacklistedExpoPrinters.includes(model.attributes.printerName),
        );
        sections.push({
          header: 'Expo Printers',
          models: filteredExpoPrinterList,
        });
      }
    } else {
      // Stati page
      const kiosksByLocationId = _.groupBy(this._kioskList.models, (kiosk) => {
        return kiosk.get('locationId');
      });
      const sortedGroups = _.sortBy(_.values(kiosksByLocationId), (kioskGroup) => {
        const location = app.orgList
          ? app.orgList.getLocationById(kioskGroup[0].get('locationId'))
          : app.locationList.get(kioskGroup[0].get('locationId'));
        return `${location.get('orgName')} ${location.nameWithChannel().toLowerCase()}`;
      });
      sections = _.map(sortedGroups, (kioskGroup) => {
        const location = app.orgList
          ? app.orgList.getLocationById(kioskGroup[0].get('locationId'))
          : app.locationList.get(kioskGroup[0].get('locationId'));
        const orderChannelTag = buildOrderChannelTag(
          location.get('orderChannel'),
          location.get('channelName'),
          {
            sessionUserIsBite: app.sessionUser.isBite(),
            isLiveLocation: location.isLive(),
            isCanary: location.isCanary(),
            isGarconCanary: location.isGarconCanary(),
          },
        );
        let header = `${location.get('name')} ${orderChannelTag}`;
        if (!app.org) {
          header = `${location.get('orgName')} ${header}`;
        }
        return {
          header,
          headerUrl: `${location.bureauUrl()}#kiosks`,
          headerAccessory: `${i9nBadgesHtml(location.getAllI9nSchemas())} | ${LocationStateHelper.name(location.get('state'))}`,
          models: kioskGroup,
        };
      });
    }

    return sections;
  },

  /**
   * @param {KioskAvailability} availability
   * @param {string} toastMessage
   * @param {string} alertPrompt
   * @param {JQuery<HTMLElement>} $dropdown
   */
  _handleChangeAvailabilityText(availability, toastMessage, alertPrompt, $dropdown) {
    const kiosk = _.first(this._kioskList.models);
    const existingClosedMessage = kiosk
      ? kiosk.get('availabilityText')[availability]
      : 'We are currently closed.\nPlease come back later!';

    const $alertContent = $(
      '<textarea style="margin: 20px 0px 5px; height: 110px; width: 100%" />',
    );
    $alertContent.val(existingClosedMessage);
    const confirmCallback = () => {
      const newClosedMessage = $alertContent.val().trim();
      if (newClosedMessage && newClosedMessage !== existingClosedMessage) {
        app.showSavedToast(toastMessage);
        this._kioskList.changeAvailabilityText(newClosedMessage, availability, {
          success() {
            app.showSavedToast('Success!');
          },
        });
      }
    };
    const alertView = new app.AlertView();
    alertView.show(alertPrompt, $alertContent, confirmCallback, () => {});
    $dropdown.toggleClass('open');
    return false;
  },

  _restartAppOnAllKiosks() {
    const disconnectedKioskNames = this._kioskList.models
      .filter((kiosk) => kiosk.pusherIsDisconnected())
      .map((kiosk) => kiosk.displayName())
      .sort();
    let confirmation = `Are you sure you want to restart the app on all kiosks?`;
    if (disconnectedKioskNames.length) {
      confirmation += `\n\nOnly kiosks connected to pusher can receive this command.\nCurrently the following kiosks are disconnected: ${disconnectedKioskNames}`;
    }
    if (!window.confirm(confirmation)) {
      return;
    }

    app.showSavedToast('Restarting...');

    async.forEachLimit(
      this._kioskList.models,
      4,
      (kiosk, cb) => {
        kiosk.notifyDevice(KioskNotificationType.RestartApp, null, {
          error(err) {
            cb(err);
          },
          success() {
            cb();
          },
        });
      },
      (err) => {
        if (err) {
          app.showSavedToast('Failed. :(');
        } else {
          app.showSavedToast('Success!');
        }
      },
    );
  },

  _restartAllKiosksThroughMdm() {
    const confirmation = `Are you sure you want to restart all kiosks?\n(The kiosks need to be online in their MDM for this to work.)`;
    if (!window.confirm(confirmation)) {
      return;
    }

    app.showSavedToast('Restarting...');
    async.forEachLimit(
      this._kioskList.models,
      4,
      (kiosk, cb) => {
        kiosk.restartDeviceThroughMDM({
          error(err) {
            cb(err);
          },
          success() {
            cb();
          },
        });
      },
      (err) => {
        if (err) {
          app.showSavedToast('Failed. :(');
        } else {
          app.showSavedToast('Success!');
        }
      },
    );
  },

  _loginToAllKiosksThroughMdm() {
    const disconnectedKioskNames = this._kioskList.models
      .filter((kiosk) => kiosk.pusherIsDisconnected())
      .map((kiosk) => kiosk.displayName())
      .sort();
    let confirmation = `Are you sure you want to enter the menu on all kiosks?`;
    if (disconnectedKioskNames.length) {
      confirmation += `\n\nOnly kiosks connected to pusher can receive this command.\nCurrently the following kiosks are disconnected: ${disconnectedKioskNames}`;
    }
    if (!window.confirm(confirmation)) {
      return;
    }

    app.showSavedToast('Logging in and entering the menu...');

    async.forEachLimit(
      this._kioskList.models,
      4,
      (kiosk, cb) => {
        kiosk.notifyDevice(KioskNotificationType.AppLogin, null, {
          error(err) {
            cb(err);
          },
          success() {
            cb();
          },
        });
      },
      (err) => {
        if (err) {
          app.showSavedToast('Failed. :(');
        } else {
          app.showSavedToast('Success!');
        }
      },
    );
  },

  _renderToolbar() {
    const isBiteOps = app.sessionUser.isBiteOps();
    const $toolbar = $(
      // prettier-ignore
      '<div class="container kiosk-toolbar">' +
        '<div class="btn-group">' +
          '<div class="btn btn-secondary btn-sm refresh">' +
            '<i class="bi bi-arrow-clockwise"></i>&nbsp;Refresh' +
          '</div>' +
          '<div class="btn btn-secondary btn-sm show-only-issues">' +
            '<i class="bi bi-exclamation-triangle"></i>&nbsp;<span class="title">Show Only Issues</span>' +
          '</div>' +
          '<div class="btn btn-secondary btn-sm show-only-live">' +
            '<i class="bi bi-exclamation-triangle"></i>&nbsp;<span class="title">Show Only Live</span>' +
          '</div>' +
        '</div>' +
        '<div class="change-all-kiosks dropdown">' +
          '<button type="button" class="btn btn-secondary btn-sm">Change All Kiosks...</button>' +
          '<ul class="dropdown-menu collapsed dropdown-menu-right">' +
            '<li class="item turn-on"><a>Turn on</a></li>' +
            '<li class="item turn-off-until-next"><a>Temporarily close until next service</a></li>' +
            '<li class="item turn-off-indefinitely"><a>Turn off indefinitely</a></li>' +
            '<li class="item change-availability-text"><a>Change closed screen copy</a></li>' +
            '<li class="item change-temp-availability-text"><a>Change temporarily closed screen copy</a></li>' +
            '<li class="item critical restart-app"><a>Restart App</a></li>' +
            '<li class="item critical restart-device"><a>Restart Device</a></li>' +
            (isBiteOps ? '<li class="item critical login-device"><a>Login to Device</a></li>' : '') +
          '</ul>' +
        '</div>' +
      '</div>',
    );
    this.$listHeader.html($toolbar);

    $toolbar.find('.btn.refresh').click(() => {
      this.collection.fetch({ reset: true });
    });
    this._$showOnlyIssuesButton = $toolbar.find('.btn.show-only-issues');
    if (app.location) {
      this._$showOnlyIssuesButton.remove();
    } else {
      this._$showOnlyIssuesButton.click(() => {
        const currentSearchQuery = (this.$searchField.val() || '').trim();
        const token = ':show-only-issues';
        if (currentSearchQuery.includes(token)) {
          this.$searchField.val(currentSearchQuery.replace(token, '').trim());
        } else {
          this.$searchField.val(`${token} ${currentSearchQuery}`);
        }
        this.$searchField.trigger('search');
      });
    }

    this._$showOnlyLiveButton = $toolbar.find('.btn.show-only-live');
    if (app.location || !app.org) {
      this._$showOnlyLiveButton.remove();
    } else {
      this._$showOnlyLiveButton.click(() => {
        const currentSearchQuery = (this.$searchField.val() || '').trim();
        const token = ':show-only-live';
        if (currentSearchQuery.includes(token)) {
          this.$searchField.val(currentSearchQuery.replace(token, '').trim());
        } else {
          this.$searchField.val(`${token} ${currentSearchQuery}`);
        }
        this.$searchField.trigger('search');
      });
    }

    const $dropdown = $toolbar.find('.change-all-kiosks');

    if (app.sessionUser.canControlKiosks()) {
      $dropdown.find('button').click(() => {
        $dropdown.toggleClass('open');
      });

      $dropdown.find('.turn-on a').click(() => {
        this._clickHandlerForAvailability(
          $dropdown,
          KioskAvailability.ON,
          '',
          'Turning On All Kiosks',
        );
      });
      $dropdown.find('.turn-off-until-next a').click(() => {
        this._clickHandlerForAvailability(
          $dropdown,
          KioskAvailability.OFF_UNTIL_NEXT,
          'Until Next Service',
          'Turning Off All Kiosks',
        );
      });
      $dropdown.find('.turn-off-indefinitely a').click(() => {
        this._clickHandlerForAvailability(
          $dropdown,
          KioskAvailability.OFF_INDEFINITELY,
          'Indefinitely',
          'Turning Off All Kiosks',
        );
      });
      $dropdown.find('.change-availability-text a').click(() => {
        return this._handleChangeAvailabilityText(
          KioskAvailability.OFF_INDEFINITELY,
          'Changing Closed Screen Copy...',
          'Please enter a new copy for the closed screen:',
          $dropdown,
        );
      });
      $dropdown.find('.change-temp-availability-text a').click(() => {
        return this._handleChangeAvailabilityText(
          KioskAvailability.OFF_UNTIL_NEXT,
          'Changing Temporarily Closed Screen Copy...',
          'Please enter a new copy for the temporarily closed screen:',
          $dropdown,
        );
      });
      $dropdown.find('.restart-app a').click(() => {
        this._restartAppOnAllKiosks();
        $dropdown.toggleClass('open');
        return false;
      });
      $dropdown.find('.restart-device a').click(() => {
        this._restartAllKiosksThroughMdm();
        $dropdown.toggleClass('open');
        return false;
      });
      if (isBiteOps) {
        $dropdown.find('.login-device a').click(() => {
          this._restartAllKiosksThroughMdm();
          $dropdown.toggleClass('open');
          return false;
        });
      }
    } else {
      $dropdown.find('button').prop('disabled', true);
    }
  },

  _renderSearchHint() {
    const keywordsByLabel = {
      POS: I9nHelper.getSearchTokensWithTypes(['pos', 'pos-data-source'], ':standalone'),
      Fulfillment: I9nHelper.getSearchTokensWithTypes(['fulfillment']),
      Payments: I9nHelper.getSearchTokensWithTypes([
        'ecomm-payment',
        'kiosk-payment',
        'cash-machine',
      ]),
      Loyalty: I9nHelper.getSearchTokensWithTypes(['loyalty', 'loyalty-earner', '']),
      'Stored Value': I9nHelper.getSearchTokensWithTypes([
        'stored-value',
        'comp-card',
        'campus-card',
      ]),
      Marketing: I9nHelper.getSearchTokensWithTypes(['marketing']),
      'Table Tracker': I9nHelper.getSearchTokensWithTypes(['table-tracker']),
      OS: [':Android', ':ChromeOS', ':iOS', ':Tizen'],
      MDM: [':EloView', ':Esper', ':WorkspaceOne', ':SignageOS', ':SimpleMDM', ':ChromeOS'],
      Features: [':Canary', ':Lab', ':Pilot'],
      Peripherals: [
        ':Has-Printer',
        ':No-Printer',
        ':Has-HID',
        ':Has-MSR',
        ':Has-Scanner',
        ':Has-Storm-Audio-Nav',
      ],
      Issues: [
        ':show-only-issues',
        ':Missing',
        ':Missing-Payment',
        ':Missing-Peripherals',
        ':Outdated-Build',
        ':Outdated-OS',
        ':Outdated-WebView',
        ':Pusher-Disconnected',
        ':Unhandled-Model',
        ':Wrong-DPI',
      ],
    };

    const countByKeyword = {};
    if (this.collection.hasBeenFetched()) {
      _.each(keywordsByLabel, (keywords) => {
        _.each(keywords, (keyword) => {
          const count = this.collection.filter((kiosk) => {
            return kiosk.matchesQuery(keyword);
          }).length;
          countByKeyword[keyword] = count;
        });
      });

      if (app.org) {
        // If we are looking at the org-level list of kiosks, then we might as well filter out all
        // the options that this org will never have.
        // Keep features, peripherals, and issues since anyone can have those.
        const labelsToPrune = [
          'POS',
          'Fulfillment',
          'Payments',
          'Loyalty',
          'Stored Value',
          'OS',
          'MDM',
          'Marketing',
          'Table Tracker',
        ];
        labelsToPrune.forEach((label) => {
          const applicableKeywords = keywordsByLabel[label].filter((keyword) => {
            return (countByKeyword[keyword] || 0) > 0;
          });
          if (applicableKeywords.length > 1) {
            keywordsByLabel[label] = applicableKeywords;
          } else {
            delete keywordsByLabel[label];
          }
        });
      }
    }

    app.HtmlHelper.renderSearchHints({
      keywordsByLabel,
      keywordUsageExample: ':olo :worldpay for all kiosks in olo locations with WP',
      $searchHint: this.$searchHint,
      $searchField: this.$searchField,
      searchFieldEventToTriggerOnTokenClick: 'search',
      countByKeyword,
      skipZeroCounts: true,
    });
  },

  _renderList() {
    app.BaseListView.prototype._renderList.apply(this, arguments);

    const query = this._getCurrentQuery();
    this._$showOnlyIssuesButton
      ?.find('.title')
      .text(query.includes(':show-only-issues') ? 'Show All Kiosks' : 'Show Only Issues');
    this._$showOnlyLiveButton
      ?.find('.title')
      .text(query.includes(':show-only-live') ? 'Show All Channels' : 'Show Only Live');
  },

  // Reliably start the download and allows us to set the file name.
  _exportKioskList() {
    const csvLines = getCsvLinesFromKioskList({
      kioskList: this._kioskList,
      getLocationById: (locationId) => {
        if (app.orgList) {
          return app.orgList.getLocationById(locationId);
        }
        return app.locationList.get(locationId);
      },
      shouldIncludeLocation: (location) => {
        if (app.location?.id === location.id) {
          return true;
        }
        return location.get('state') === LocationState.Live;
      },
      shouldIncludeKiosk: (kiosk) => {
        const status = kiosk.get('status');
        if (!status) {
          return true;
        }
        const timeSinceLastUpdate = Date.now() - (status.lastStatusReceivedAt || status.updatedAt);
        return timeSinceLastUpdate < Time.WEEK * 4;
      },
    });

    let filename = 'bite-wide-kiosk-list';
    if (app.location) {
      filename = `bite-kiosk-list-${app.org.get('normalizedName')}-${app.location.get('normalizedName')}`;
    } else if (app.org) {
      filename = `bite-kiosk-list-${app.org.get('normalizedName')}`;
    }

    const timestamp = TimeHelper.format(Date.now(), 'YYYY-MM-DD-HH-mm');
    const aLink = document.createElement('a');
    aLink.download = `${filename}-${timestamp}.csv`;
    aLink.href = convertCsvLinesToCsvData(csvLines);
    const event = new MouseEvent('click');
    aLink.dispatchEvent(event);
  },

  render() {
    app.BaseListView.prototype.render.apply(this, arguments);

    this.$el.addClass('kiosk-list');

    if (!this.collection.hasBeenFetched()) {
      this.collection.fetch({ reset: true });
      this.listenToOnce(this.collection, 'reset', () => {
        if (app.sessionUser.isBite()) {
          this._renderSearchHint();
        }
      });

      // Render again once the locations/orgs are loaded on org-kiosks/bite-kiosks pages resp.
      if (app.org && !app.location && !app.locationList.hasBeenFetched()) {
        this.listenToOnce(app.locationList, 'reset', this._renderList);
      } else if (!app.org && !app.location && !app.orgList.hasBeenFetched()) {
        this.listenToOnce(app.orgList, 'reset', this._renderList);
      }
    }

    if (app.org) {
      this._renderToolbar();
    }

    if (!app.location && app.sessionUser.isBite()) {
      this._renderSearchHint();
    }

    if (app.sessionUser.isBiteOps()) {
      const $downloadButton = $(
        // prettier-ignore
        '<button type="button" class="btn btn-sm btn-primary download">' +
          'Download as CSV' +
        '</button>',
      );
      this.$('.card-header .right-button-container').prepend($downloadButton);
      $downloadButton.click(() => {
        this._exportKioskList();
      });
    }

    const createButton = this.$('.card-header .right-button-container button.create');
    createButton.html(`${createButton.html()}${app.HtmlHelper.resellerRightIcon}`);

    if (app.location && app.sessionUser.isBiteOps()) {
      const $transferButton = $(
        `<button type="button" class="btn btn-sm btn-primary transfer">Transfer kiosk ${app.HtmlHelper.biteRightIcon}</button>`,
      );
      createButton.before($transferButton);
      $transferButton.click(() => {
        app.modalManager.showModalWithView(
          new app.KioskTransferModal({
            onTransfer: () => {
              this.collection.fetch({ reset: true });
            },
          }),
        );
      });
    }

    return this;
  },
});
